import { useTranslation } from 'react-i18next'
import type { Conflict, FieldConflict } from '@/services/merge'
import type { Choice } from '@/services/merge/resolve'

interface FieldDiffProps {
  conflict: Conflict
  currentChoice: Choice
  fieldChoices?: Record<string, Choice>
  onChoose: (choice: Choice) => void
  onFieldChoose: (field: string, choice: Choice) => void
}

export function FieldDiff({
  conflict,
  currentChoice,
  fieldChoices,
  onChoose,
  onFieldChoose,
}: FieldDiffProps) {
  const { t } = useTranslation()
  const fields = conflict.fields ?? []

  if (fields.length === 0) {
    // Block choice only
    return (
      <div className="flex gap-3 mt-1.5">
        <SideChoice
          side="mine"
          label={t('conflict.mine', 'o meu')}
          value={String((conflict.mineSnapshot as Record<string, unknown>)?.name ?? conflict.uid)}
          chosen={currentChoice === 'mine'}
          onChoose={() => onChoose('mine')}
        />
        <SideChoice
          side="theirs"
          label={t('conflict.theirs', 'o deles')}
          value={String((conflict.theirsSnapshot as Record<string, unknown>)?.name ?? conflict.uid)}
          chosen={currentChoice === 'theirs'}
          onChoose={() => onChoose('theirs')}
        />
      </div>
    )
  }

  return (
    <div className="space-y-1.5 mt-1.5">
      {/* Block-level choose-all buttons */}
      <div className="flex gap-1.5 mb-2">
        <button
          type="button"
          onClick={() => onChoose('mine')}
          className="text-[10px] rounded border px-2 py-0.5 transition-colors"
          style={{
            borderColor: 'var(--border)',
            background: currentChoice === 'mine' && !fieldChoices ? 'var(--accent)' : 'var(--bg-card)',
            color: currentChoice === 'mine' && !fieldChoices ? '#fff' : 'var(--text-muted)',
          }}
        >
          {t('conflict.useAllMine', 'usar tudo: o meu')}
        </button>
        <button
          type="button"
          onClick={() => onChoose('theirs')}
          className="text-[10px] rounded border px-2 py-0.5 transition-colors"
          style={{
            borderColor: 'var(--border)',
            background: currentChoice === 'theirs' && !fieldChoices ? 'var(--accent)' : 'var(--bg-card)',
            color: currentChoice === 'theirs' && !fieldChoices ? '#fff' : 'var(--text-muted)',
          }}
        >
          {t('conflict.useAllTheirs', 'usar tudo: o deles')}
        </button>
      </div>
      {fields.map((fc) => (
        <FieldRow
          key={fc.field}
          fc={fc}
          fieldChoice={fieldChoices?.[fc.field] ?? currentChoice}
          onMine={() => onFieldChoose(fc.field, 'mine')}
          onTheirs={() => onFieldChoose(fc.field, 'theirs')}
        />
      ))}
    </div>
  )
}

function FieldRow({
  fc,
  fieldChoice,
  onMine,
  onTheirs,
}: {
  fc: FieldConflict
  fieldChoice: Choice
  onMine: () => void
  onTheirs: () => void
}) {
  return (
    <div className="grid grid-cols-[100px_1fr_1fr] gap-2 items-center text-[11px]">
      <span className="text-right font-mono" style={{ color: 'var(--text-dim)' }}>
        {fc.field}
      </span>
      <RadioValue
        label={String(fc.mine ?? '—')}
        chosen={fieldChoice === 'mine'}
        onClick={onMine}
        data-testid={`field-mine-${fc.field}`}
      />
      <RadioValue
        label={String(fc.theirs ?? '—')}
        chosen={fieldChoice === 'theirs'}
        onClick={onTheirs}
        data-testid={`field-theirs-${fc.field}`}
      />
    </div>
  )
}

function SideChoice({
  label,
  value,
  chosen,
  onChoose,
}: {
  side: Choice
  label: string
  value: string
  chosen: boolean
  onChoose: () => void
}) {
  return (
    <button
      type="button"
      onClick={onChoose}
      className="flex-1 rounded-lg border px-3 py-2 text-left text-[11px] transition-colors"
      style={{
        borderColor: chosen ? 'var(--accent)' : 'var(--border)',
        background: chosen ? 'var(--accent-muted, color-mix(in srgb, var(--accent) 10%, transparent))' : 'var(--bg-card)',
        color: chosen ? 'var(--text)' : 'var(--text-dim)',
      }}
    >
      <div className="font-medium mb-0.5" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div className="font-mono truncate">{value}</div>
    </button>
  )
}

function RadioValue({
  label,
  chosen,
  onClick,
  'data-testid': testId,
}: {
  label: string
  chosen: boolean
  onClick: () => void
  'data-testid'?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="flex items-center gap-1.5 rounded border px-2 py-1 text-left w-full transition-colors"
      style={{
        borderColor: chosen ? 'var(--accent)' : 'var(--border)',
        background: chosen ? 'var(--accent-muted, color-mix(in srgb, var(--accent) 10%, transparent))' : 'var(--bg-card)',
        color: chosen ? 'var(--text)' : 'var(--text-dim)',
        opacity: chosen ? 1 : 0.6,
      }}
    >
      <span
        className="inline-block h-3 w-3 rounded-full border flex-shrink-0"
        style={{
          borderColor: chosen ? 'var(--accent)' : 'var(--text-muted)',
          background: chosen ? 'var(--accent)' : 'transparent',
        }}
        aria-hidden="true"
      />
      <span className="font-mono truncate">{label}</span>
    </button>
  )
}
